import { API_URL, API_PREFIX } from "../config/api";
import {
    getAccessToken,
    getRefreshToken,
    getStoredUser,
    setSessionTokens,
    clearSession,
} from "./tokenStorage";

export const AUTH_CLEARED_EVENT = "hr:auth-cleared";

let authCleared = false;
let refreshPromise = null;
const TRANSIENT_GATEWAY_STATUSES = new Set([502, 503, 504]);
const TRANSIENT_RETRY_DELAY_MS = 500;
const SERVICE_UNAVAILABLE_MESSAGE =
    "Сервис временно недоступен. Повторите попытку через несколько секунд.";
const PAYLOAD_TOO_LARGE_MESSAGE =
    "Файл слишком большой. Максимальный размер одного документа — 10 МБ.";

const VALIDATION_FIELD_LABELS = {
    full_name: "Ф.И.О.",
    birth_date: "Дата рождения",
    passport_series: "Серия паспорта",
    passport_number: "Номер паспорта",
    passport_issue_date: "Дата выдачи паспорта",
    passport_issued_by: "Кем выдан паспорт",
    passport_department_code: "Код подразделения",
    registration_address: "Адрес регистрации",
    actual_residence_address: "Фактический адрес проживания",
    snils_number: "СНИЛС",
    inn_number: "ИНН",
    bank_account: "Расчётный счёт",
    bank_name: "Название банка",
    bank_bik: "БИК",
    correspondent_account: "Корреспондентский счёт",
    bank_recipient_name: "Получатель",
    salary: "Итоговая зарплата",
    legal_entity: "Юридическое лицо",
    employment_date: "Дата трудоустройства",
    position: "Должность",
    work_schedule: "График работы",
    offer_text: "Текст оффера",
    file: "Файл",
};

function validationFieldLabel(item) {
    const raw = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
    return VALIDATION_FIELD_LABELS[raw] || (raw ? String(raw).replaceAll("_", " ") : "Значение");
}

function formatValidationIssue(item) {
    if (typeof item === "string") return item;
    if (!item || typeof item !== "object") return String(item);
    const label = validationFieldLabel(item);
    const limit = item.ctx?.max_length ?? item.ctx?.min_length;
    if (/[А-Яа-яЁё]/.test(item.msg || "")) {
        return String(item.msg).replace(/^Value error,\s*/i, "");
    }
    const messages = {
        missing: `Поле «${label}» обязательно для заполнения`,
        string_too_long: `Поле «${label}» должно содержать не более ${limit} символов`,
        string_too_short: `Поле «${label}» должно содержать не менее ${limit} символов`,
        string_type: `В поле «${label}» нужно ввести текст`,
        int_parsing: `В поле «${label}» нужно ввести целое число`,
        float_parsing: `В поле «${label}» нужно ввести число`,
        decimal_parsing: `В поле «${label}» нужно ввести число`,
        decimal_max_places: `Поле «${label}» должно содержать не более ${item.ctx?.decimal_places ?? 2} знаков после запятой`,
        decimal_whole_digits: `Поле «${label}» содержит слишком большое число`,
        greater_than: `Значение поля «${label}» должно быть больше ${item.ctx?.gt ?? 0}`,
        literal_error: `Выберите допустимое значение поля «${label}»`,
        date_from_datetime_parsing: `В поле «${label}» указана некорректная дата`,
        date_parsing: `В поле «${label}» указана некорректная дата`,
        value_error: `Поле «${label}» заполнено некорректно`,
    };
    if (messages[item.type]) return messages[item.type];
    return `Поле «${label}» заполнено некорректно`;
}

export function resetAuthClearedFlag() {
    authCleared = false;
}

function formatErrorDetail(detail, fallback) {
    if (detail == null || detail === "") return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map(formatValidationIssue)
            .filter(Boolean)
            .join("; ") || fallback;
    }
    if (typeof detail === "object") {
        return detail.msg || detail.message || JSON.stringify(detail);
    }
    return String(detail);
}

function responseError(res, detail) {
    const error = new Error(detail);
    error.status = res?.status;
    error.statusText = res?.statusText;
    error.detail = detail;
    return error;
}

function looksLikeHtml(value) {
    return /^\s*(?:<!doctype\s+html|<html)[\s>]/i.test(String(value || ""));
}

function responseErrorDetail(res, data, text) {
    if (TRANSIENT_GATEWAY_STATUSES.has(res?.status)) {
        // Application endpoints can use 502 to report a rejected upstream
        // operation (for example a closed HH chat). Preserve an explicit JSON
        // explanation; only hide empty/HTML gateway responses.
        const upstreamDetail = data?.detail ?? data?.message;
        if (upstreamDetail != null && upstreamDetail !== "") {
            return formatErrorDetail(upstreamDetail, SERVICE_UNAVAILABLE_MESSAGE);
        }
        return SERVICE_UNAVAILABLE_MESSAGE;
    }
    if (res?.status === 413) {
        return formatErrorDetail(data?.detail ?? data?.message, PAYLOAD_TOO_LARGE_MESSAGE);
    }
    if (looksLikeHtml(text)) {
        return `Сервер вернул ошибку HTTP ${res?.status || ""}. Повторите попытку позже.`.trim();
    }
    return formatErrorDetail(data?.detail ?? data?.message, text || res?.statusText);
}

async function waitBeforeTransientRetry(signal) {
    await new Promise((resolve) => setTimeout(resolve, TRANSIENT_RETRY_DELAY_MS));
    if (signal?.aborted) {
        const abortErr = new Error("Aborted");
        abortErr.name = "AbortError";
        throw abortErr;
    }
}

/** HH OAuth / upstream auth must not wipe the HR platform session. */
function isUpstreamAuthFailure(detail) {
    const msg = String(detail || "").toLowerCase();
    return (
        msg.includes("hh token")
        || msg.includes("hh.ru")
        || msg.includes("hh authorization")
        || msg.includes("hh api")
        || msg.includes("ошибка авторизации hh")
        || msg.includes("не сессия hr")
        || (msg.includes("авторизац") && msg.includes("hh"))
        || msg.includes("ai service auth")
    );
}

function handleUnauthorized() {
    if (authCleared) return;
    if (!getAccessToken() && !getRefreshToken()) return;
    authCleared = true;
    clearSession();
    try {
        window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
    } catch {
        /* ignore */
    }
    if (window.location.pathname !== "/") {
        window.location.replace("/");
    }
}

/**
 * Single-flight refresh: concurrent 401s share one /token/refresh call.
 * Survives multi-tab races: if another tab already rotated, treat local tokens as OK.
 * @returns {Promise<boolean>} true if tokens were renewed (or already renewed elsewhere)
 */
export async function refreshSession() {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    if (!refreshPromise) {
        refreshPromise = (async () => {
            const startedWith = refreshToken;
            try {
                const url = `${API_URL}${API_PREFIX}/token/refresh`;
                const res = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ refresh_token: startedWith }),
                    mode: "cors",
                    cache: "no-store",
                });
                const text = await res.text();
                let data = null;
                if (text) {
                    try {
                        data = JSON.parse(text);
                    } catch {
                        data = null;
                    }
                }
                if (res.ok && data?.access_token) {
                    setSessionTokens(data);
                    return true;
                }
            } catch {
                // Network / parse — fall through to cross-tab check
            }

            // Another tab may have rotated the same family and written new tokens.
            const latestRefresh = getRefreshToken();
            const latestAccess = getAccessToken();
            if (latestAccess && latestRefresh && latestRefresh !== startedWith) {
                return true;
            }
            return false;
        })().finally(() => {
            refreshPromise = null;
        });
    }

    try {
        return await refreshPromise;
    } catch {
        const latestRefresh = getRefreshToken();
        const latestAccess = getAccessToken();
        if (latestAccess && latestRefresh && latestRefresh !== refreshToken) {
            return true;
        }
        return false;
    }
}

async function parseResponseBody(res) {
    if (!res || typeof res.text !== "function") {
        throw new Error("Пустой ответ от API (сеть или прокси)");
    }
    const text = await res.text();
    let data = null;
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            if (res.status === 401) {
                data = null;
            } else {
                data = res.ok ? text : null;
            }
        }
    }
    return { data, text };
}

async function tryRefreshAndRetry(retryFn, detail) {
    if (isUpstreamAuthFailure(detail)) return { refreshed: false, shouldClear: false };
    if (!getRefreshToken()) return { refreshed: false, shouldClear: Boolean(getAccessToken()) };
    const refreshed = await refreshSession();
    if (refreshed) {
        return { refreshed: true, shouldClear: false, result: await retryFn() };
    }
    return { refreshed: false, shouldClear: true };
}

function actorHeaders() {
    const user = getStoredUser();
    if (!user || typeof user !== "object") return {};
    const id = user.erp_user_id ?? user.id;
    const name = user.full_name || user.name || user.username || user.login;
    const headers = {};
    if (id != null && String(id).trim()) headers["X-Actor-Id"] = String(id).trim();
    if (name && String(name).trim()) headers["X-Actor-Name"] = encodeURIComponent(String(name).trim());
    return headers;
}

export async function request(path, { auth = true, method = "GET", headers = {}, body, signal, _retried = false, _netRetried = false } = {}) {
    const url = `${API_URL}${API_PREFIX}${path.startsWith("/") ? path : `/${path}`}`;
    const token = getAccessToken();

    const finalHeaders = {
        ...(auth ? actorHeaders() : {}),
        ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
    };

    let finalBody = body;
    if (body && !(body instanceof FormData) && !(body instanceof URLSearchParams)) {
        finalHeaders["Content-Type"] = finalHeaders["Content-Type"] || "application/json";
        finalBody = JSON.stringify(body);
    }

    let res;
    try {
        res = await fetch(url, {
            method,
            headers: finalHeaders,
            body: finalBody,
            mode: "cors",
            cache: "no-store",
            ...(signal ? { signal } : {}),
        });
    } catch (err) {
        if (signal?.aborted || err?.name === "AbortError") {
            const abortErr = new Error("Aborted");
            abortErr.name = "AbortError";
            throw abortErr;
        }
        const msg = err?.message || String(err);
        if (/failed to fetch|networkerror|load failed/i.test(msg)) {
            // Aborted fetches are often reported as Failed to fetch in Chromium.
            if (signal?.aborted) {
                const abortErr = new Error("Aborted");
                abortErr.name = "AbortError";
                throw abortErr;
            }
            // One automatic retry for transient network blips
            if (!_netRetried && method === "GET") {
                await new Promise((r) => setTimeout(r, 500));
                if (signal?.aborted) {
                    const abortErr = new Error("Aborted");
                    abortErr.name = "AbortError";
                    throw abortErr;
                }
                return request(path, { auth, method, headers, body, signal, _retried, _netRetried: true });
            }
            throw new Error(
                "Нет связи с сервером. Проверьте подключение к интернету или VPN и повторите попытку"
            );
        }
        throw err instanceof Error ? err : new Error(msg);
    }

    if (TRANSIENT_GATEWAY_STATUSES.has(res.status) && method === "GET" && !_netRetried) {
        await waitBeforeTransientRetry(signal);
        return request(path, { auth, method, headers, body, signal, _retried, _netRetried: true });
    }

    const { data, text } = await parseResponseBody(res);

    if (res.status === 401) {
        const detail = formatErrorDetail(data?.detail ?? data?.message, text || "Unauthorized");
        if (isUpstreamAuthFailure(detail)) {
            throw new Error(detail || "Unauthorized");
        }
        if (auth && !_retried) {
            const outcome = await tryRefreshAndRetry(
                () => request(path, { auth, method, headers, body, signal, _retried: true, _netRetried }),
                detail
            );
            if (outcome.refreshed) return outcome.result;
            if (outcome.shouldClear) handleUnauthorized();
        }
        // After a successful refresh, a second 401 is endpoint/business auth — do not wipe the HR session.
        throw new Error(detail || "Unauthorized");
    }

    if (!res.ok) {
        throw responseError(
            res,
            responseErrorDetail(res, data, text)
        );
    }
    return data;
}

export async function requestBlob(path, { auth = true, method = "GET", headers = {}, body, signal, _retried = false, _netRetried = false } = {}) {
    const url = `${API_URL}${API_PREFIX}${path.startsWith("/") ? path : `/${path}`}`;
    const token = getAccessToken();

    const finalHeaders = {
        ...(auth ? actorHeaders() : {}),
        ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
    };

    let finalBody = body;
    if (body && !(body instanceof FormData) && !(body instanceof URLSearchParams)) {
        finalHeaders["Content-Type"] = finalHeaders["Content-Type"] || "application/json";
        finalBody = JSON.stringify(body);
    }

    const res = await fetch(url, { method, headers: finalHeaders, body: finalBody, signal, mode: "cors", cache: "no-store" });

    if (TRANSIENT_GATEWAY_STATUSES.has(res.status) && !_netRetried) {
        await waitBeforeTransientRetry();
        return requestBlob(path, { auth, method, headers, body, signal, _retried, _netRetried: true });
    }

    if (res.status === 401) {
        if (auth && !_retried) {
            const outcome = await tryRefreshAndRetry(
                () => requestBlob(path, { auth, method, headers, body, signal, _retried: true, _netRetried }),
                "Unauthorized"
            );
            if (outcome.refreshed) return outcome.result;
            if (outcome.shouldClear) handleUnauthorized();
        }
        throw new Error("Unauthorized");
    }

    if (!res.ok) {
        const text = await res.text();
        let data = null;
        try {
            data = text ? JSON.parse(text) : null;
        } catch {
            data = null;
        }
        throw responseError(res, responseErrorDetail(res, data, text));
    }

    return res.blob();
}

export const http = {
    get: (path, opts = {}) => request(path, { ...opts, method: "GET" }),
    post: (path, body, opts = {}) => request(path, { ...opts, method: "POST", body }),
    put: (path, body, opts = {}) => request(path, { ...opts, method: "PUT", body }),
    patch: (path, body, opts = {}) => request(path, { ...opts, method: "PATCH", body }),
    del: (path, opts = {}) => request(path, { ...opts, method: "DELETE" }),
    getBlob: (path, opts = {}) => requestBlob(path, opts),
    postBlob: (path, body, opts = {}) => requestBlob(path, { ...opts, method: "POST", body }),
};
